import React, { useState } from "react";
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from "@mui/material";
import { FEATURES } from "../../../../config/features";
import { useFilterManager } from "../../../hooks/useFilterManager";
import { useEmployeeTypeTagging } from "../../../hooks/useEmployeeTypeTagging";
import { DynamicFilterBar } from "../../filters/DynamicFilterBar";
import { Button, SearchBar } from "../../../../common/APICommon";
import EmployeeTypeTaggingTable from "./components/EmployeeTypeTaggingTable";
import EmployeeTypeTaggingModal from "./components/EmployeeTypeTaggingModal";

const EmployeeTypeTaggingModule = ({ user, showGeneralMessage }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const { activeFilterState, onFilterChange, apiFilterPayload, applyFilters, resetFilters } = useFilterManager(FEATURES.EMPLOYEE_FILTERS || []);

  const { loading, data, handleSearch, createTagging } = useEmployeeTypeTagging({
    showGeneralMessage,
    apiFilterPayload,
    user
  });

  const handleSave = async (form) => {
    const success = await createTagging(form);
    if (success) setIsModalOpen(false);
  };

  return (
    <div className="p-4 space-y-4">
      <div className="text-sm flex items-center justify-between">
        <div>
          <span className="text-gray-400">{"Transaction > "}</span>
          <span className="border-b border-black text-gray-700">{"Employee Type Tagging"}</span>
        </div>

        {/* MODIFIED: Added slotProps to handle width constraints */}
        <Tooltip
          slotProps={{
            popper: {
              sx: {
                zIndex: 15000, // ADDED: Higher than the top bar
              },
            },
            tooltip: {
              sx: {
                maxWidth: 400,
                backgroundColor: '#1e293b',
                padding: '12px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.4)', // ADDED: Depth for visibility
                '& .MuiTooltip-arrow': {
                  color: '#1e293b',
                },
              },
            },
          }}
          title={
            <div className="text-xs leading-relaxed">
              <p className="font-bold border-b border-gray-500 mb-2 pb-1 text-blue-300">
                Policy Configuration Guide
              </p>
              <p className="mb-2">
                This module manages temporary employee type assignments. These records
                control how attendance logs are generated:
              </p>
              <ul className="pl-4 list-disc space-y-1">
                <li>
                  <b className="text-blue-200">Fixed Logs:</b> Enabled for types with
                  <i> "Allow Fixed Logs"</i> (e.g., Offshore). Logs are auto-generated
                  based on schedules.
                </li>
                <li>
                  <b className="text-blue-200">Actual Logs:</b> Default policy for
                  standard types. Logs are pulled from Biometric/Mobile devices.
                </li>
              </ul>
              <p className="mt-2 pt-2 border-t border-gray-500 italic text-gray-300">
                * The system identifies the most recent effective record for each date.
              </p>
            </div>
          }
          arrow
          placement="left"
        >
          <InfoIcon className="text-gray-400 hover:text-blue-600 cursor-help transition-colors" fontSize="small" />
        </Tooltip>
      </div>
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-xl font-bold text-customMainColor uppercase my-0 me-2">Employee Type Tagging</h3>
        <Button
          name="ADD NEW"
          icon={<AddIcon />}
          className="bg-customGreen hover:bg-customGreenHover text-white border-none px-4 py-2 rounded shadow cursor-pointer font-bold"
          onClick={() => { setSelectedRow(null); setIsModalOpen(true); }}
        />
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm" style={{ border: "1px solid #d7d7d7" }}>
        <span className="text-lg font-semibold text-gray-600 block mb-4">Filters</span>
        <DynamicFilterBar
          requestedFilters={FEATURES.EMPLOYEE_FILTERS || []}
          activeFilterState={activeFilterState}
          onFilterChange={onFilterChange}
          showButtons={false}
        />
        <div className="flex gap-2 mt-5 border-t pt-4">
          <Button name="GENERATE" className="bg-blue-600 text-white px-5 py-2 border-none rounded cursor-pointer font-bold" onClick={applyFilters} />
          <Button name="CLEAR" className="bg-gray-400 text-white px-5 py-2 border-none rounded cursor-pointer" onClick={resetFilters} />
        </div>
      </div>

      <SearchBar handleFilter={handleSearch} />

      <EmployeeTypeTaggingTable
        data={data}
        onView={(row) => { setSelectedRow(row); setIsModalOpen(true); }}
      />

      <EmployeeTypeTaggingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        loading={loading}
        selectedData={selectedRow}
      />
    </div>
  );
};

export default EmployeeTypeTaggingModule;